import LumaDialog from '../../../components/admin/LumaDialog';
import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { useAuth } from '../../../context/AuthContext.tsx';
import { isUserSuperAdmin } from '../../../utils/userTier.ts';
import { 
    fetchRamasProfesionales, 
    createRamaProfesional, 
    updateRamaProfesional, 
    deleteRamaProfesional, 
    agregarSubrama, 
    eliminarSubrama, 
    RamaProfesional 
} from '../../../services/ramaProfesionalService.ts';
import { 
    ShieldAlert, 
    Plus, 
    Edit2, 
    Trash2, 
    RefreshCw, 
    Check, 
    X, 
    Tag, 
    Sparkles, 
    Layers, 
    ArrowLeft 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RamasProfesionalesPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isSuperAdmin = isUserSuperAdmin(user);

    const [deleteTarget,setDeleteTarget]=useState<RamaProfesional|null>(null);
    const [deleting,setDeleting]=useState(false);
    const [ramas, setRamas] = useState<RamaProfesional[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Estado para nueva sub-rama por rama id
    const [newSubramaText, setNewSubramaText] = useState<{ [key: number]: string }>({});
    const [submittingSubrama, setSubmittingSubrama] = useState<{ [key: number]: boolean }>({});

    // Modal de edición o creación de rama
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRama, setEditingRama] = useState<RamaProfesional | null>(null);
    const [formData, setFormData] = useState({
        nombre: '',
        codigo: '',
        icono: '🏗️',
        color: '#3564db',
        color_dark: '#3564db',
        orden: 1,
    });
    const [savingRama, setSavingRama] = useState(false);

    const loadRamas = useCallback(async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const data = await fetchRamasProfesionales();
            setRamas(data);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setErrorMsg('No se pudieron cargar las ramas profesionales.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isSuperAdmin) {
            loadRamas();
        }
    }, [isSuperAdmin, loadRamas]);

    const showToast = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(null), 3500);
    };

    const handleOpenCreateModal = () => {
        setEditingRama(null);
        setFormData({
            nombre: '',
            codigo: '',
            icono: '💼',
            color: '#3564db',
            color_dark: '#3564db',
            orden: ramas.length + 1,
        });
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (rama: RamaProfesional) => {
        setEditingRama(rama);
        setFormData({
            nombre: rama.nombre,
            codigo: rama.codigo,
            icono: rama.icono || '💼',
            color: rama.color || '#3564db',
            color_dark: rama.color_dark || '#3564db',
            orden: rama.orden || 1,
        });
        setIsModalOpen(true);
    };

    const handleSaveRama = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingRama(true);
        try {
            if (editingRama) {
                const updated = await updateRamaProfesional(editingRama.id, formData);
                setRamas(prev => prev.map(r => r.id === updated.id ? updated : r));
                showToast(`Rama "${updated.nombre}" actualizada con éxito.`);
            } else {
                const created = await createRamaProfesional({
                    ...formData,
                    subramas: [],
                    activo: true,
                });
                setRamas(prev => [...prev, created]);
                showToast(`Rama "${created.nombre}" creada con éxito.`);
            }
            setIsModalOpen(false);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setErrorMsg('Hubo un error al guardar la rama.');
        } finally {
            setSavingRama(false);
        }
    };

    const handleDeleteRama = async (rama: RamaProfesional) => {

        try {
            await deleteRamaProfesional(rama.id);
            setRamas(prev => prev.filter(r => r.id !== rama.id));
            showToast(`Rama "${rama.nombre}" eliminada.`);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setErrorMsg('No se pudo eliminar la rama.');
        }
    };

    const handleAddSubrama = async (ramaId: number) => {
        const text = (newSubramaText[ramaId] || '').trim();
        if (!text) return;
        setSubmittingSubrama(prev => ({ ...prev, [ramaId]: true }));
        try {
            const updated = await agregarSubrama(ramaId, text);
            setRamas(prev => prev.map(r => r.id === updated.id ? updated : r));
            setNewSubramaText(prev => ({ ...prev, [ramaId]: '' }));
            showToast(`Sub-rama "${text}" agregada correctamente.`);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setErrorMsg('No se pudo agregar la sub-rama.');
        } finally {
            setSubmittingSubrama(prev => ({ ...prev, [ramaId]: false }));
        }
    };

    const handleRemoveSubrama = async (ramaId: number, subramaName: string) => {
        try {
            const updated = await eliminarSubrama(ramaId, subramaName);
            setRamas(prev => prev.map(r => r.id === updated.id ? updated : r));
            showToast(`Sub-rama "${subramaName}" eliminada.`);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setErrorMsg('No se pudo eliminar la sub-rama.');
        }
    };

    if (!isSuperAdmin) {
        return (
            <div style={{ padding: '60px 24px', maxWidth: '680px', margin: '0 auto', textAlign: 'center', fontFamily: "'DM Sans', sans-serif" }}>
                <div style={{
                    background: 'var(--ad-surface, #fff)', borderRadius: '24px', padding: '48px 32px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)', border: '1.5px solid #fed7aa',
                }} className="card custom-card vx-card">
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#ea580c' }}>
                        <ShieldAlert size={32} />
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight:'650', color: 'var(--ad-ink, #0f172a)', marginBottom: '8px' }}>
                        Acceso Restringido a Super Administradores
                    </h2>
                    <p style={{ fontSize: '0.92rem', color: 'var(--ad-muted, #64748b)', lineHeight: '1.6', marginBottom: '24px' }}>
                        Solo el personal con rol de <strong>Super Admin</strong> puede configurar las ramas principales y sub-ramas profesionales del ecosistema Co Marca.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        style={{
                            background: 'var(--ad-secondary, #3564db)', color: '#fff', border: 'none', borderRadius: '12px',
                            padding: '12px 28px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer',
                        }}
                    >
                        Volver al Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return <><LumaDialog open={!!deleteTarget} title="Eliminar registro" onClose={()=>{if(!deleting)setDeleteTarget(null);}}><p>¿Deseas eliminar la rama {deleteTarget?.nombre}?</p><div className="luma-resource-toolbar" style={{marginTop:24}}><button type="button" className="vx-button vx-button-secondary" disabled={deleting} onClick={()=>setDeleteTarget(null)}>Cancelar</button><button type="button" className="vx-button vx-danger" disabled={deleting} onClick={()=>{if(!deleteTarget||deleting)return;setDeleting(true);void handleDeleteRama(deleteTarget).finally(()=>{setDeleting(false);setDeleteTarget(null);});}}>{deleting?'Eliminando…':'Sí, eliminar'}</button></div></LumaDialog><LumaResourceWorkspace title="Ramas profesionales" description="Consulta y gestiona la información de tu coworking." group="Contenido" rows={ramas} columns={[{key:'nombre',label:'Nombre de la rama'},{key:'codigo',label:'Código'},{key:'icono',label:'Icono'},{key:'color',label:'Color principal'},{key:'color_dark',label:'Color secundario'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={true} onCreate={()=>{handleOpenCreateModal()}} onEdit={handleOpenEditModal} onDelete={setDeleteTarget}><details className="luma-period-metrics"><summary>Administrar subramas</summary>{ramas.map(r=><section className="luma-resource-panel" key={r.id}><h3>{r.nombre}</h3><div className="luma-row-actions">{r.subramas.map(sub=><button key={sub} type="button" onClick={()=>handleRemoveSubrama(r.id,sub)}>{sub} ×</button>)}</div><div className="luma-resource-toolbar"><input aria-label={`Nueva subrama de ${r.nombre}`} value={newSubramaText[r.id]||''} onChange={e=>setNewSubramaText(v=>({...v,[r.id]:e.target.value}))}/><button type="button" className="vx-button vx-button-primary" disabled={submittingSubrama[r.id]} onClick={()=>handleAddSubrama(r.id)}>Agregar subrama</button></div></section>)}</details></LumaResourceWorkspace>{isModalOpen && (
                <div data-luma-overlay="true" style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
                }}>
                    <div style={{
                        background: 'var(--ad-surface, #fff)', borderRadius: '24px', maxWidth: '500px', width: '100%',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.25)', overflow: 'hidden', border: '1.5px solid var(--ad-line, #f1f5f9)',
                    }} className="card custom-card vx-card">
                        <div style={{
                            background: 'linear-gradient(135deg, var(--ad-secondary, #3564db) 0%, var(--ad-primary, #3564db) 100%)',
                            padding: '24px 28px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight:'650', margin: 0 }}>
                                    {editingRama ? 'Editar Rama Profesional' : 'Nueva Rama Profesional'}
                                </h3>
                                <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: '2px' }}>
                                    Categoría principal para el Banco de Profesionales
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                                    width: '32px', height: '32px', color: '#fff', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveRama} style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: 'var(--ad-ink, #334155)', marginBottom: '6px' }}>
                                    Nombre de la Rama *
                                </label>
                                <input
                                    type="text" required
                                    value={formData.nombre}
                                    onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="Ej: Ingeniería & Arquitectura"
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px',
                                        border: '1.5px solid var(--ad-line, #e2e8f0)', fontSize: '0.9rem', outline: 'none',
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '14px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: 'var(--ad-ink, #334155)', marginBottom: '6px' }}>
                                        Código (ID único) *
                                    </label>
                                    <input
                                        type="text" required
                                        value={formData.codigo}
                                        onChange={e => setFormData(prev => ({ ...prev, codigo: e.target.value.toLowerCase().trim() }))}
                                        placeholder="ej: ingenieria"
                                        disabled={!!editingRama}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: '1.5px solid var(--ad-line, #e2e8f0)', fontSize: '0.9rem', outline: 'none',
                                            background: editingRama ? 'var(--ad-soft, #f8fafc)' : '#fff',
                                        }}
                                    />
                                </div>
                                <div style={{ width: '90px' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: 'var(--ad-ink, #334155)', marginBottom: '6px' }}>
                                        Icono
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.icono}
                                        onChange={e => setFormData(prev => ({ ...prev, icono: e.target.value }))}
                                        placeholder="🏗️"
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: '1.5px solid var(--ad-line, #e2e8f0)', fontSize: '1.2rem', textAlign: 'center', outline: 'none',
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '14px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: 'var(--ad-ink, #334155)', marginBottom: '6px' }}>
                                        Color Principal
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="color"
                                            value={formData.color}
                                            onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                                            style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }}
                                        />
                                        <input
                                            type="text"
                                            value={formData.color}
                                            onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                                            style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: '1.5px solid var(--ad-line, #e2e8f0)', fontSize: '0.85rem' }}
                                        />
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: 'var(--ad-ink, #334155)', marginBottom: '6px' }}>
                                        Color Secundario
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="color"
                                            value={formData.color_dark}
                                            onChange={e => setFormData(prev => ({ ...prev, color_dark: e.target.value }))}
                                            style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }}
                                        />
                                        <input
                                            type="text"
                                            value={formData.color_dark}
                                            onChange={e => setFormData(prev => ({ ...prev, color_dark: e.target.value }))}
                                            style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: '1.5px solid var(--ad-line, #e2e8f0)', fontSize: '0.85rem' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    style={{
                                        padding: '10px 18px', borderRadius: '10px', border: '1.5px solid var(--ad-line, #e2e8f0)',
                                        background: 'var(--ad-soft, #f8fafc)', color: 'var(--ad-muted, #64748b)', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingRama}
                                    style={{
                                        background: 'linear-gradient(135deg, var(--ad-secondary, #3564db) 0%, var(--ad-primary, #3564db) 100%)',
                                        color: '#fff', border: 'none', borderRadius: '10px',
                                        padding: '10px 22px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                                    }} className="btn btn-primary"
                                >
                                    {savingRama ? 'Guardando...' : (editingRama ? 'Guardar Cambios' : 'Crear Rama')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}</>;
};

export default RamasProfesionalesPage;

